import React, { useState, useRef } from 'react'
import { Download, Copy, Search, Volume2, Save, Edit3 } from 'lucide-react'
import Button from '../ui/Button'
import Input from '../ui/Input'
import AudioPlayer from '../audio/AudioPlayer'
import SpeakerSegment from './SpeakerSegment'
import { audioService } from '../../services/audioService'
import { transcriptionService } from '../../services/transcriptionService'

const TranscriptionViewer = ({ transcription, onTranscriptionUpdate }) => {
  const [searchTerm, setSearchTerm] = useState('')
  const [highlightedSegment, setHighlightedSegment] = useState(null)
  const [audioUrl, setAudioUrl] = useState(null)
  const [loadingAudio, setLoadingAudio] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [editedText, setEditedText] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  const [hasChanges, setHasChanges] = useState(false)
  const audioPlayerRef = useRef(null)

  // 搜索过滤片段
  const filteredSegments = transcription.segments?.filter(segment =>
    segment.text.toLowerCase().includes(searchTerm.toLowerCase()) ||
    segment.speaker_id.toLowerCase().includes(searchTerm.toLowerCase())
  ) || []

  // 复制转录文本
  const handleCopyText = async () => {
    try {
      const text = transcriptionService.exportAsText(transcription)
      await navigator.clipboard.writeText(text)
      alert('文本已复制到剪贴板')
    } catch (error) {
      console.error('复制失败:', error)
      alert('复制失败')
    }
  }

  // 下载转录结果
  const handleDownload = () => {
    transcriptionService.downloadTranscription(transcription)
  }

  // 加载音频
  const handleLoadAudio = async () => {
    if (audioUrl || !transcription.audio_file_path) return

    try {
      setLoadingAudio(true)
      const url = await audioService.getAudioFileUrl(transcription.audio_file_path)
      setAudioUrl(url)
    } catch (error) {
      console.error('加载音频失败:', error)
      alert('加载音频失败')
    } finally {
      setLoadingAudio(false)
    }
  }

  // 跳转到指定片段
  const handleSegmentClick = (segment) => {
    setHighlightedSegment(segment.id)
    
    // 如果有音频播放器，跳转到对应时间
    if (audioPlayerRef.current && audioUrl) {
      audioPlayerRef.current.seekTo(segment.start_time)
    } else if (!audioUrl) {
      // 如果没有加载音频，提示用户
      handleLoadAudio()
    }
  }

  // 音频时间更新时高亮对应片段
  const handleAudioTimeUpdate = (currentTime) => {
    const currentSegment = transcription.segments?.find(segment =>
      currentTime >= segment.start_time && currentTime <= segment.end_time
    )
    
    if (currentSegment) {
      setHighlightedSegment(currentSegment.id)
    }
  }

  // 开始编辑
  const handleStartEdit = () => {
    setIsEditing(true)
    setEditedText(transcriptionService.getDisplayText(transcription))
    setHasChanges(false)
  }

  // 取消编辑
  const handleCancelEdit = () => {
    setIsEditing(false)
    setEditedText('')
    setHasChanges(false)
  }

  // 文本变化处理
  const handleTextChange = (e) => {
    const newText = e.target.value
    setEditedText(newText)
    setHasChanges(newText !== transcriptionService.getDisplayText(transcription))
  }

  // 保存编辑
  const handleSaveEdit = async () => {
    if (!hasChanges || !editedText.trim()) return

    try {
      setIsSaving(true)
      await transcriptionService.saveEditedText(transcription.id, editedText)
      
      // 通知父组件更新
      if (onTranscriptionUpdate) {
        onTranscriptionUpdate()
      }
      
      setIsEditing(false)
      setHasChanges(false)
    } catch (error) {
      console.error('保存编辑失败:', error)
      alert('保存失败：' + error.message)
    } finally {
      setIsSaving(false)
    }
  }

  // 键盘快捷键（只在文本编辑区域内生效）
  const handleTextAreaKeyDown = (e) => {
    // Ctrl+S 或 Cmd+S 保存
    if ((e.ctrlKey || e.metaKey) && e.key === 's') {
      e.preventDefault() // 只阻止保存页面，不影响其他功能
      if (hasChanges) {
        handleSaveEdit()
      }
      return
    }
    
    // ESC 取消编辑
    if (e.key === 'Escape') {
      e.preventDefault() // 只阻止默认ESC行为
      handleCancelEdit()
      return
    }
  }

  if (!transcription) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500">转录数据不可用</p>
      </div>
    )
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* 头部信息 */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              {transcription.original_filename}
            </h1>
            <div className="flex items-center space-x-4 text-sm text-gray-500">
              <span>说话者: {transcription.speaker_count || 0} 位</span>
              <span>创建时间: {new Date(transcription.created_at).toLocaleString()}</span>
              {transcription.completed_at && (
                <span>完成时间: {new Date(transcription.completed_at).toLocaleString()}</span>
              )}
              {transcription.is_edited && transcription.edited_at && (
                <span className="inline-flex items-center px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">
                  已校对 • {new Date(transcription.edited_at).toLocaleString()}
                </span>
              )}
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleCopyText}
              title="复制文本"
            >
              <Copy className="h-4 w-4" />
            </Button>
            
            <Button
              variant="outline"
              size="sm"
              onClick={handleDownload}
              title="下载文档"
            >
              <Download className="h-4 w-4" />
            </Button>

            <Button
              variant="outline"
              size="sm"
              onClick={handleLoadAudio}
              loading={loadingAudio}
              title="加载音频"
            >
              <Volume2 className="h-4 w-4" />
            </Button>

            {/* 编辑按钮 */}
            {!isEditing ? (
              <Button
                variant="outline"
                size="sm"
                onClick={handleStartEdit}
                title="编辑文档"
              >
                <Edit3 className="h-4 w-4" />
              </Button>
            ) : (
              <div className="flex items-center space-x-2">
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleSaveEdit}
                  loading={isSaving}
                  disabled={!hasChanges}
                  title={isSaving ? '保存中...' : '保存修改'}
                >
                  <Save className="h-4 w-4" />
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleCancelEdit}
                  disabled={isSaving}
                  title="取消编辑"
                >
                  ✕
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* 音频播放器 */}
        {audioUrl && (
          <AudioPlayer
            ref={audioPlayerRef}
            audioUrl={audioUrl}
            onTimeUpdate={handleAudioTimeUpdate}
            className="mt-4"
          />
        )}
      </div>

      {/* 搜索和过滤 */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="flex items-center space-x-4">
          <div className="flex-1">
            <Input
              placeholder="搜索转录内容或说话者..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full"
            />
          </div>
          <Search className="h-5 w-5 text-gray-400" />
        </div>

        {searchTerm && (
          <p className="text-sm text-gray-500 mt-2">
            找到 {filteredSegments.length} 个匹配结果
          </p>
        )}
      </div>

      {/* 转录内容 */}
      <div className="bg-white border border-gray-200 rounded-lg">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">转录内容</h2>
            {isEditing && (
              <div className="flex items-center gap-3">
                {hasChanges && (
                  <span className="flex items-center gap-2 text-sm text-amber-600">
                    <div className="w-2 h-2 bg-amber-500 rounded-full"></div>
                    有未保存的更改
                  </span>
                )}
                <span className="text-xs text-gray-500">
                  文本框内: Ctrl+S 保存 / ESC 取消
                </span>
              </div>
            )}
          </div>
        </div>

        <div className="divide-y divide-gray-200">
          {isEditing ? (
            // 编辑模式 - 显示可编辑文本框
            <div className="p-6">
              <div className="relative">
                <textarea
                  value={editedText}
                  onChange={handleTextChange}
                  onKeyDown={handleTextAreaKeyDown}
                  className="w-full h-96 p-4 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="编辑转录文本..."
                  disabled={isSaving}
                />
                
                {/* 字数统计 */}
                <div className="absolute bottom-2 right-2 text-xs text-gray-400 bg-white px-2 py-1 rounded">
                  {editedText.length} 字符
                </div>
              </div>
              
              {/* 编辑说明 */}
              <div className="mt-4 text-xs text-gray-500 bg-gray-50 p-3 rounded-lg">
                <div className="font-medium mb-1">编辑说明:</div>
                <ul className="space-y-1">
                  <li>• 可以直接修改任何转录错误的文字</li>
                  <li>• 在文本框内按 Ctrl+S 快速保存，按 ESC 取消编辑</li>
                  <li>• 快捷键仅在文本编辑区域生效，不影响音频播放</li>
                  <li>• 支持边听音频边编辑文本</li>
                  <li>• 修改后的文档会标记为"已校对"状态</li>
                </ul>
              </div>
            </div>
          ) : (
            // 查看模式 - 显示转录内容
            filteredSegments.length > 0 ? (
              filteredSegments.map((segment) => (
                <SpeakerSegment
                  key={segment.id}
                  segment={segment}
                  isHighlighted={highlightedSegment === segment.id}
                  onClick={handleSegmentClick}
                  className="px-6 py-4"
                />
              ))
            ) : transcriptionService.getDisplayText(transcription) ? (
              <div className="p-6">
                <div className="prose max-w-none">
                  <pre className="whitespace-pre-wrap font-sans text-gray-900 leading-relaxed">
                    {transcriptionService.getDisplayText(transcription)}
                  </pre>
                </div>
              </div>
            ) : (
              <div className="p-6 text-center text-gray-500">
                暂无转录内容
              </div>
            )
          )}
        </div>
      </div>
    </div>
  )
}

export default TranscriptionViewer